import { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardContent } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { catchError, throwError } from 'rxjs';
import _ from 'lodash';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useIsInternalUser } from '../../../hooks/useIsInternalUser';
import type { Driver } from '../../../data/models/driver.model';
import * as driverSlice from '../../../data/driver.slice';
import * as referenceSlice from '../../../data/reference.slice';
import PhoneInput from '../../form/PhoneInput';
import validationUtils from '../../../services/validationUtils';
import TextInput from '../../form/TextInput';
import SelectMenu from '../../form/SelectMenu';
import CheckboxInput from '../../form/CheckboxInput';
import DateInput from '../../form/DateInput';
import driverUtils from '../../../services/driverUtils';
import ElementDialog, { formClasses } from '../ElementDialog';
import ActiveClear from '../../form/ActiveClear';
import Name from '../../form/Name';
import documentUtils from '../../../services/documentUtils';
import type { BaseProvider } from '../../../data/models/provider.model';
import { useRequireDriversLicense } from '../../../hooks/useRequireDriversLicense';
import { useEnforceDriversLicenseFormat } from '../../../hooks/useEnforceDriversLicenseFormat';
import userService from '../../../services/userService';
import emailUtils from '../../../services/emailUtils';
import { DateTime } from 'luxon';

interface DriverDialogProps {
  title: string;
  open: boolean;
  provider: BaseProvider;
  driver?: Driver;
  close: (saved: boolean) => void;
}

export default function DriverDialog({
  title,
  open,
  provider,
  driver,
  close,
}: DriverDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');
  const requiredUsernameFieldMessage = t('drivers.usernameRequired');
  const requireDriversLicense = useRequireDriversLicense();
  const enforceDriversLicenseFormat = useEnforceDriversLicenseFormat();
  const isInternalUser = useIsInternalUser();
  const isProviderInternal = provider?.isInternal;

  const [errorMsgEmail, setErrorMsgEmail] = useState<string>();
  const [errorMsgUsername, setErrorMsgUsername] = useState<string>();
  const [minBirthDate, setMinBirthDate] = useState<DateTime>();
  const [maxBirthDate, setMaxBirthDate] = useState<DateTime>();
  const [providerIsCTT, setProviderIsCTT] = useState(false);
  const [disabledMessage, setDisabledMessage] = useState('');

  const stateCodes = useAppSelector(referenceSlice.selectStateCodes);
  const messagingMethods = useAppSelector(
    referenceSlice.selectMessagingMethods,
  );
  const documentTypes = useAppSelector(
    referenceSlice.selectDriverDocumentTypes,
  );
  const tripModes = useAppSelector(referenceSlice.selectTripModes);

  const userRolesIds = useAppSelector(driverSlice.selectUserRoleIds);

  const documents = useAppSelector(driverSlice.selectDriverDocuments);

  const saving = useAppSelector(driverSlice.selectSavingDriver);
  const saved = useAppSelector(driverSlice.selectSavedDriver);
  const savingError = useAppSelector(driverSlice.selectSavingError);

  const { control, formState, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: driverUtils.getDefaults(driver),
    mode: 'onTouched',
  });

  const isCTTChecked = watch('IsClearToTransport');
  const dlState = watch('DriverLicenseIssuedInStateCode');
  const isInternalChecked = watch('IsInternal');

  const validateEmail = useAppSelector(driverSlice.getValidateEmail);
  const validateUsername = useAppSelector(driverSlice.getValidateUsername);
  const validateFields = useAppSelector(driverSlice.getValidateFields);

  useEffect(() => {
    setMinBirthDate(DateTime.fromISO('1950-01-01'));
    setMaxBirthDate(DateTime.now().endOf('day'));
    dispatch(referenceSlice.fetchTripModesAsync());
    dispatch(driverSlice.getUserRolesAsync()); // TODO: this type shouldn't be hard coded
  }, []);

  // This is here so after a save, the updated driver is used to populate the form values
  useEffect(() => {
    reset(driverUtils.getDefaults(driver));
  }, [driver, reset]);

  const [hasDocuments, setHasDocuments] = useState(false);
  useEffect(() => {
    if (documentTypes && documents) {
      setHasDocuments(
        documentUtils.hasAllRequiredDocuments(documentTypes, documents),
      );
    }
  }, [documentTypes, documents]);

  useEffect(() => {
    const ctt = !!provider?.isClearToTransport;
    setProviderIsCTT(ctt);
  }, [driver, provider]);

  useEffect(() => {
    if (isInternalUser && isProviderInternal) {
      setValue('IsInternal', true);
    }
  }, [isInternalUser, isProviderInternal, setValue]);

  useEffect(() => {
    let msg = '';
    if (!isCTTChecked) {
      // For now there are no required driver documents so this is commented out.
      // if (!driver || !hasDocuments) {
      //   msg += t('common.missingDocuments') + ' ';
      // }
      if (!providerIsCTT) {
        msg += t('common.providerNotCTT');
      }
    }
    setDisabledMessage(msg);
  }, [isCTTChecked, hasDocuments, providerIsCTT, driver, t]);

  useEffect(() => {
    if (driver?.driverID) {
      dispatch(driverSlice.fetchDriverDocumentsAsync(driver.driverID));
    } else {
      dispatch(driverSlice.setDriverDocuments([]));
    }
    dispatch(driverSlice.setValidateFields(''));
    dispatch(driverSlice.setValidateEmail(false));
    dispatch(driverSlice.setValidateUsername(false));
  }, [driver, dispatch]);

  const saveDriver = useCallback(
    (data: any) => {
      const id = driver ? driver.driverID : undefined;

      const d = driverUtils.getDriverFromFormData(
        provider.transportationProviderID!,
        id,
        data,
        driver?.emailAddresses,
        driver?.phoneNumbers,
        userRolesIds,
        // id ? driverUtils.getDriverUsername(driver) : watch('PreferredUserName'),
      );

      const oldEmail = emailUtils.getDriverEmail(driver?.emailAddresses);

      const newEmail = watch('Email');
      const needValid: boolean = oldEmail?.EmailAddress !== newEmail;

      let fn = driverSlice.createDriverAsync(d);
      if (id && driver) {
        const driverUserID = driver.userAccountDriver?.userAccountID;

        fn = driverSlice.updateDriverAsync(d, needValid, driverUserID);
      }

      dispatch(fn);
    },
    [provider, userRolesIds],
  );

  const validateEmailChange = useCallback(
    _.debounce((e: any) => {
      const newEmail = e.target.value;

      if (!newEmail) {
        return;
      }

      dispatch(driverSlice.setValidateFields('email'));

      userService
        .validateEmailUsername('email', newEmail)
        .pipe(
          catchError((err) => {
            dispatch(driverSlice.setValidateEmail(false));

            setErrorMsgEmail(err.message);
            return throwError(() => new Error(err.message));
          }),
        )
        .subscribe((d: any) => {
          if (d.error) {
            setErrorMsgEmail(d.error);
            dispatch(driverSlice.setValidateEmail(false));
            return throwError(() => new Error(d.error));
          } else {
            dispatch(driverSlice.setValidateEmail(true));
          }
        });
    }, 2000),
    [],
  );

  const validateUsernameChange = useCallback(
    _.debounce((e: any) => {
      const username = e.target.value;

      if (!username) {
        return;
      }

      dispatch(driverSlice.setValidateFields('username'));

      userService
        .validateEmailUsername('username', username)
        .pipe(
          catchError((err) => {
            dispatch(driverSlice.setValidateUsername(false));
            setErrorMsgUsername(err.message);
            return throwError(() => new Error(err.message));
          }),
        )
        .subscribe((d: any) => {
          if (d.error) {
            setErrorMsgUsername(d.error);
            dispatch(driverSlice.setValidateUsername(false));
            return throwError(() => new Error(d.error));
          } else {
            dispatch(driverSlice.setValidateUsername(true));
          }
        });
    }, 2000),
    [],
  );

  return (
    <ElementDialog
      title={title}
      faqContext="drivers_details_form"
      open={open}
      saving={saving}
      saved={saved}
      savingError={savingError}
      save={handleSubmit(saveDriver)}
      reset={reset}
      close={close}
      formState={formState}>
      <Card className={formClasses.card}>
        <CardHeader title={t('common.contactInfo')} />
        <CardContent className={formClasses.cardContent}>
          <Name
            control={control}
            formState={formState}
            requiredFieldMessage={requiredFieldMessage}
            showMiddleName={true}
          />
          <TextInput
            label={t('common.email')}
            name="Email"
            required={true}
            control={control}
            errors={formState.errors}
            rules={{
              required: requiredFieldMessage,
              pattern: validationUtils.emailValidation(),
            }}
            inputProps={{
              maxLength: 255,
              onChange: (e: any) => {
                setErrorMsgEmail('');
                validateEmailChange(e);
              },
            }}
          />
          {validateFields.includes('email') && !validateEmail ? (
            <span style={{ color: 'red' }}>{errorMsgEmail}</span>
          ) : (
            ''
          )}
          {validateFields.includes('email') && validateEmail ? (
            <span style={{ color: 'green' }}>
              {t('drivers.emailAvailable')}
            </span>
          ) : (
            ''
          )}
          {!driver && (
            <>
              <TextInput
                label="Username"
                name="PreferredUserName"
                required={true}
                control={control}
                errors={formState.errors}
                inputProps={{
                  required: true,
                  maxLength: 255,
                  onChange: (e: any) => {
                    setErrorMsgUsername('');
                    validateUsernameChange(e);
                  },
                }}
                rules={{
                  required: requiredUsernameFieldMessage,
                  pattern: validationUtils.usernameValidation(),
                }}
              />

              {!formState.errors.PreferredUserName &&
              validateFields.includes('username') &&
              !validateUsername ? (
                <span style={{ color: 'red' }}>{errorMsgUsername}</span>
              ) : (
                ''
              )}
              {!formState.errors.PreferredUserName &&
              validateFields.includes('username') &&
              validateUsername ? (
                <span style={{ color: 'green' }}>
                  {t('drivers.usernameAvailable')}
                </span>
              ) : (
                ''
              )}
            </>
          )}
          <PhoneInput
            label={t('driver.primaryPhone')}
            name="DriverPhone"
            extensionName="DriverPhoneExtension"
            required={true}
            control={control}
            errors={formState.errors}
          />
          <PhoneInput
            label={t('driver.secondaryPhone')}
            name="SecondaryDriverPhone"
            extensionName="SecondaryDriverPhoneExtension"
            required={false}
            control={control}
            errors={formState.errors}
          />
          <SelectMenu
            label={t('driver.messagingMethod')}
            name="MessagingMethod"
            values={messagingMethods}
            valueAttr="messagingMethodID"
            labelAttr="messagingMethodName"
            required={true}
            control={control}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
          />
          <CheckboxInput
            label={t('common.hasSMSConsent')}
            name="HasSMSConsent"
            required={false}
            control={control}
          />
        </CardContent>
      </Card>
      <Card className={formClasses.card}>
        <CardHeader title={t('driver.info')} />
        <CardContent className={formClasses.cardContent}>
          <DateInput
            label={t('driver.dateOfBirth')}
            name="DriverDateOfBirth"
            control={control}
            required={false}
            errors={formState.errors}
            minDate={minBirthDate}
            maxDate={maxBirthDate}
          />
          <TextInput
            label={t('driver.dlNumber')}
            name="DriversLicenseNumber"
            required={requireDriversLicense}
            control={control}
            errors={formState.errors}
            inputProps={{ maxLength: 50 }}
            tooltip={t('driver.dlTooltip') || ''}
            rules={{
              required: requireDriversLicense
                ? requiredFieldMessage
                : undefined,
              validate:
                requireDriversLicense && enforceDriversLicenseFormat
                  ? validationUtils.driversLicenseValidation(dlState, t)
                  : undefined,
            }}
          />
          <SelectMenu
            label={t('driver.dlState')}
            name="DriverLicenseIssuedInStateCode"
            values={stateCodes}
            valueAttr="stateCode"
            labelAttr="stateName"
            required={requireDriversLicense}
            control={control}
            errors={formState.errors}
            rules={{
              required: requireDriversLicense
                ? requiredFieldMessage
                : undefined,
            }}
          />
          <DateInput
            label={t('driver.dlExpiration')}
            name="DriverLicenseExpiration"
            control={control}
            required={true}
            errors={formState.errors}
            minDate={DateTime.now()}
          />
          <ActiveClear
            control={control}
            hideClearToTransport={false}
            hideReadyClearToTransport={true}
            disabledMessage={disabledMessage ? disabledMessage : undefined}
            disableClearToTransport={!!disabledMessage && !isCTTChecked}
          />
          {isInternalUser && (
            <CheckboxInput
              label={t('driver.isInternal')}
              name="IsInternal"
              required={false}
              control={control}
              disabled={isProviderInternal && isInternalChecked}
            />
          )}
        </CardContent>
      </Card>
      <Card className={formClasses.card}>
        <CardHeader title={t('driver.emergencyContact')} />
        <CardContent className={formClasses.cardContent}>
          <Name
            namePrefix="EmergencyContact"
            control={control}
            formState={formState}
            required={false}
          />
          <PhoneInput
            label={t('common.phone')}
            name="EmergencyContactPhone"
            extensionName="EmergencyContactPhoneExtension"
            required={false}
            control={control}
            errors={formState.errors}
          />
        </CardContent>
      </Card>
    </ElementDialog>
  );
}
